/**
 * @voltxampere/engine — Transient source-stepping rescue (Sprint 54.3f
 * engine extension #7).
 *
 * The transient solver's per-step Newton iteration can fail to converge
 * on stiff topologies (multi-capacitor banks, nonlinear diode bridges,
 * behavioral state-machine networks) even when the adaptive-step
 * controller has shrunk h to its floor. Halving h further does not
 * help because the difficulty is not the time-derivative term — it is
 * the nonlinear residual at the current iterate.
 *
 * Source-stepping (SPECS §3.7 lineage, ported from DC to transient)
 * gradually ramps the independent sources' contribution from a small
 * fraction (10 % of nominal) up to full amplitude in 10 evenly-spaced
 * continuation steps. Each step warm-starts from the previous step's
 * iterate, so the first few solves face an effectively-easy
 * sub-circuit and the last solve picks up the actual answer.
 *
 * Sprint 5's DC source-stepping `sourceStepping()` is the algorithmic
 * cousin. The transient form differs in three ways:
 *
 *   1. Same `sourceScale` parameter, but plumbed through
 *      {@link newtonRaphsonTransient} (which threads it onward to
 *      {@link buildTransientMNA}). DC's source-stepping calls the DC
 *      Newton directly.
 *   2. Per-step rescue (called from the time-stepping loop in
 *      {@link TransientSolver.solve}) — not a pre-transient routine.
 *      Time `t`, time-step `h`, integration method, and transient
 *      history (`xStepPrev` / `xStepPrevPrev`) are passed through
 *      unchanged.
 *   3. Engages only after plain Newton has already failed (Newton
 *      threw `ConvergenceError` or returned `converged: false`). It
 *      is the last resort before the h-halving ladder; if even
 *      source-stepping cannot converge, the solver falls back to
 *      h-halving as before.
 *
 * Engine baseline note: Sprint 54.3f resets the engine baseline
 * tag from `sprint-54-3d-baseline` to `sprint-54-3f-baseline`. This
 * is the 3rd engine break of the Sprint 54 marathon (after 54.2b
 * ext #5 + 54.3d ext #6).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError } from "@voltxampere/shared";
import { newtonRaphsonTransient } from "./newton-raphson.js";
/**
 * Continuation schedule for transient source-stepping. Mirrors the DC
 * source-stepping schedule (10 evenly-spaced steps from 10 % to
 * 100 %).
 */
export const TRANSIENT_SOURCE_STEPPING_SCHEDULE = [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0,
];
/**
 * Run the source-stepping continuation for a single transient
 * time-step. Returns the result of the final (α = 1) solve, plus
 * accumulated iteration / clamp counters.
 *
 * Throws {@link ConvergenceError} if any continuation step fails to
 * converge. The caller is expected to interpret this as "even
 * source-stepping cannot rescue this step" and fall back to h-halving.
 */
export function transientSourceStepping(circuit, plan, baseOpts, config) {
    const schedule = config.schedule ?? TRANSIENT_SOURCE_STEPPING_SCHEDULE;
    let x = new Float64Array(baseOpts.x0);
    let totalIterations = 0;
    let totalClampEvents = 0;
    const stepsConverged = [];
    let lastResult;
    for (const alpha of schedule) {
        const r = newtonRaphsonTransient(circuit, plan, {
            ...baseOpts,
            maxIterations: config.maxIterations,
            x0: x,
            sourceScale: alpha,
        });
        totalIterations += r.iterations;
        totalClampEvents += r.clampEvents;
        if (!r.converged) {
            throw new ConvergenceError(`transient source stepping failed at α=${String(alpha)} after ${String(r.iterations)} iterations`, {
                meta: {
                    alpha,
                    iterations: totalIterations,
                    stepsConverged: [...stepsConverged],
                    fallback: "transient-source",
                },
            });
        }
        stepsConverged.push(alpha);
        x = r.x;
        lastResult = r;
    }
    if (lastResult === undefined) {
        // Empty schedule. Treat as success with the cold-start x0.
        return {
            result: {
                converged: true,
                iterations: 0,
                x,
                clampEvents: 0,
                finalResidualNorm: 0,
            },
            totalIterations: 0,
            totalClampEvents: 0,
            stepsConverged: [],
        };
    }
    return {
        result: lastResult,
        totalIterations,
        totalClampEvents,
        stepsConverged,
    };
}
//# sourceMappingURL=source-stepper.js.map